import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  useTheme,
} from '@mui/material';
import { Notifications } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { formattedSymbol, itemVariants } from './utils';
import { fetchRecentlyTriggeredAlerts } from '../marketsSlice';

/**
 * AlertsWidget - Displays recent triggered alerts
 * 
 * @param {Object} config - Widget configuration
 * @param {string} [config.title] - Custom title (default: "Recent Alerts")
 * @param {number|string} [config.maxHeight] - Maximum height for widget
 */
const AlertsWidget = ({ config = {} }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { recentlyTriggeredAlerts, recentlyTriggeredAlertsLoading } = useSelector((state) => state.markets);
  const isLoading = !!recentlyTriggeredAlertsLoading;

  const title = config.title || 'Recent Alerts';
  const maxHeight = config.maxHeight || '300px';

  useEffect(() => {
    dispatch(fetchRecentlyTriggeredAlerts());
  }, [dispatch]);

  return (
    <motion.div variants={itemVariants}>
      <Card sx={{ width: '100%', height: '400px', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                {title}
              </Typography>
            </Box>
            {!isLoading && (
              <Chip
                label={recentlyTriggeredAlerts.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Alerts List */}
          {isLoading ? (
            <List sx={{ px: 0 }}>
              {[0, 1, 2, 3].map((i) => (
                <ListItem key={i} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: 'var(--dwlf-ink-06)' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Skeleton width="45%" sx={{ bgcolor: 'var(--dwlf-ink-06)' }} />}
                    secondary={<Skeleton width="65%" sx={{ bgcolor: 'var(--dwlf-ink-04)' }} />}
                  />
                </ListItem>
              ))}
            </List>
          ) : recentlyTriggeredAlerts.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ flexGrow: 1, gap: 2 }}
            >
              <Notifications sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No recent alerts found
              </Typography>
              <Typography variant="caption" color="text.disabled" textAlign="center">
                Recently triggered alerts will appear here
              </Typography>
            </Box>
          ) : (
            <List sx={{ flexGrow: 1, maxHeight, overflow: 'auto', px: 0 }}>
              {recentlyTriggeredAlerts.map((alert, index) => (
                <Link 
                  key={index} 
                  to={`/markets/${formattedSymbol(alert.assetId)}`} 
                  style={{ textDecoration: 'none' }}
                >
                  <ListItem 
                    sx={{ 
                      px: 0, 
                      py: 1,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: (theme.vars || theme).palette.action.hover,
                        borderRadius: 1
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ color: 'warning.main', fontSize: 20 }}>
                        <Notifications />
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="body2" fontWeight={600} color="text.primary">
                            {alert.assetId}
                          </Typography>
                          <Chip
                            label={alert.alertType.replace(/_/g, ' ')}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '18px' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(alert.triggeredAt).toLocaleDateString()}
                          </Typography>
                          {alert.targetValue && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              • Target: {alert.targetValue}
                            </Typography>
                          )}
                          {alert.label && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              {alert.label}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </Link>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AlertsWidget;
